import { useEffect, useMemo, useState } from "react";
import {
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  DataTableEmpty,
  createColumnHelper,
  Box,
  Text,
  Icon,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
  type VisibilityState,
} from "@salestrak/ui";

import { EntityTag, SystemMarker } from "#app/components/entity";
import { RenameWorkflowDialog } from "#app/components/workflows/rename-workflow-dialog";
import {
  renderWorkflowMenuItems,
  useWorkflowMenuActions,
} from "#app/components/workflows/workflow-menu-items";
import { WorkflowShareDialog } from "#app/components/workflows/workflow-share-dialog";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { type WorkflowRow, WorkflowsListDataDocument } from "#app/lib/data";
import type { TableViewTableProps } from "#app/lib/hooks/use-table-view-settings";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type WorkflowsTableProps = Omittable<{
  where: Record<string, unknown>;
  // Undefined when the list defers ordering to the API (`deferOrderBy`) and the
  // user has not sorted — the variable is then simply omitted.
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  /**
   * Column definitions from `useWorkflowColumns` — built by the hosting page,
   * which owns the navigation handlers the cells call.
   */
  columns: DataTableColumnDef<WorkflowRow>[];
  /** Per-user order/hidden/width customization from `useTableViewSettings`. */
  tableViewProps?: TableViewTableProps;
  /**
   * Enables the actions column's built-in circle-plus show/hide column picker
   * (the roster grammar) — pass `tableView.settingsProps.onColumnVisibilityChange`.
   */
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  /**
   * Reports the query's total row count (undefined until the first result).
   * Lets the hosting page collapse its toolbar when there are no workflows
   * at all.
   */
  onTotalChange?: (total: number | undefined) => void;
  /** Opens the hosting page's delete confirmation dialog. */
  onRequestDelete: (workflow: WorkflowRow) => void;
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const h = createColumnHelper<WorkflowRow>();
const getRowId = (r: WorkflowRow) => r.workflowId;

// Name is the identity column: pinned on overflow and a locked row in the
// column picker, matching the custom-fields table.
const WORKFLOWS_PINNED_COLUMN_IDS = ["name"];

// Columns the user opts into: the identifier is the workflow's short prefix,
// read rarely, and Updated is the same low-traffic timestamp the custom-fields
// table hides.
export const WORKFLOWS_DEFAULT_HIDDEN_COLUMN_IDS = ["key", "lastUpdateDate"];

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.workflows;

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export function useWorkflowColumns(
  onEdit: (workflowId: string) => void,
  /** Opens the workflow itself (`/workflow/:id` lands on its default view).
   *  Every row is navigable: the list query and the workflow-open query scope
   *  by the same visibility set, so a workflow the viewer can see here is one
   *  they can open. */
  onOpenWorkflow: (workflowId: string) => void,
): DataTableColumnDef<WorkflowRow>[] {
  return useMemo<DataTableColumnDef<WorkflowRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        meta: { width: 320 },
        content: (workflow) => (
          <Box direction="row" align="center" gap="xs">
            <EntityTag
              entity={{
                kind: "workflow",
                id: workflow.workflowId,
                workflow: { name: workflow.name, icon: workflow.icon },
              }}
              interactive={false}
            />
            {workflow.isDefault ? (
              <SystemMarker kind="default" subject="workflow" />
            ) : null}
          </Box>
        ),
        onClickAction: (workflow) => onEdit(workflow.workflowId),
        actionLabel: () => "Edit workflow…",
      }),

      h.accessor({
        accessorKey: "key",
        header: "Identifier",
        enableSorting: true,
        meta: { minWidth: 120, textBrightness: "subtle" },
      }),

      // Count of live projects in the workflow — pressing it opens the
      // workflow at its default view (teams-table members-count grammar).
      // Not sortable: the count is loader-computed, the server can't order
      // by it.
      h.display({
        id: "issueCount",
        header: "Projects",
        meta: { minWidth: 100 },
        cell: ({ row }) => (
          <DataTableCellTrigger
            onClick={() => onOpenWorkflow(row.original.workflowId)}
          >
            <DataTableCellContent>
              <Box direction="row" align="center" gap="xs">
                <Icon name={ENTITY_KIND_ICON.issue} size="sm" color="muted" />
                <Text size="sm" variant="muted">{row.original.issueCount.toLocaleString()}</Text>
              </Box>
            </DataTableCellContent>
          </DataTableCellTrigger>
        ),
      }),

      h.date({
        accessorKey: "creationDate",
        header: "Created",
        enableSorting: true,
      }),

      h.date({
        accessorKey: "lastUpdateDate",
        header: "Updated",
        enableSorting: true,
      }),
    ],
    [onEdit, onOpenWorkflow],
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WorkflowsTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  columns,
  tableViewProps,
  onColumnVisibilityChange,
  onRequestDelete,
  onTotalChange,
}: WorkflowsTableProps) {
  // The row menus' shared handler set — wired ONCE here, never per row (the
  // mutation hooks live inside it). The share and rename dialogs are the
  // table's own: they serve any row, so both hosting pages get them for free.
  const menuActions = useWorkflowMenuActions();
  const [shareWorkflowId, setShareWorkflowId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<WorkflowRow | null>(null);

  // --- Group query state ---
  const { states, registerState, getGroupProps } = useGroupStates<WorkflowRow>();
  const allTotal = states.get("__all__")?.total;

  useEffect(() => {
    onTotalChange?.(allTotal);
  }, [allTotal, onTotalChange]);

  const groups = useMemo<DataTableGroupType<WorkflowRow>[]>(
    () => [
      {
        key: "__all__",
        label: "All workflows",
        totalCount: allTotal,
        ...getGroupProps("__all__"),
      },
    ],
    [allTotal, getGroupProps],
  );

  return (
    <>
      {/* Ungrouped query provider */}
      <GroupQueryProvider<WorkflowRow>
        groupKey="__all__"
        document={WorkflowsListDataDocument}
        variables={{ where: whereProp, orderBy: orderByProp, limit: 50 }}
        getConnection={getConnection}
        onStateChange={registerState}
      />

      <Box direction="column" fill="flex" paddingBottom="lg">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={getRowId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          // The space under the toolbar band is scrollable: rows pass through
          // it once the list scrolls.
          gutterTop="md"
          density="regular"
          pinnedColumnIds={WORKFLOWS_PINNED_COLUMN_IDS}
          paddingX="lg"
          {...tableViewProps}
          onColumnVisibilityChange={onColumnVisibilityChange}
          // The full shared workflow menu. The SAME render serves the kebab
          // dropdown and the row right-click — the table's overlay host reuses
          // it for both, and Base UI's context-menu items ARE its menu items,
          // so the dropdown parts work inside either popup.
          renderRowActions={(workflow) =>
            renderWorkflowMenuItems({
              workflow,
              actions: menuActions,
              canShare: workflow.viewerCanManageSharing,
              onShare: () => setShareWorkflowId(workflow.workflowId),
              onRename: () => setRenameTarget(workflow),
              onRequestDelete: () => onRequestDelete(workflow),
            })
          }
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No workflows match your search"
                  : "No workflows found"
              }
            />
          }
        />
      </Box>

      <WorkflowShareDialog
        onOpenChange={(open) => {
          if (!open) setShareWorkflowId(null);
        }}
        workflowId={shareWorkflowId}
      />
      <RenameWorkflowDialog
        workflow={renameTarget}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
      />
    </>
  );
}
